import { Entity, EntityMetricsDefinition, KeyValuePair, SmartModuleType, SortOrder, TimeSeriesAggregation } from '@moderepo/mode-apis';
import { Time } from '@moderepo/mode-app-components';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { isObjectLiteral, NoneValue, TypeNone } from '../../utils/utils';


// The Home KV Key to be used for storing the dashboard
export const HOME_DASHBOARDS_KV_KEY = 'MODE_dashboards';


// Define how many columns, based on the 12 columns system, to use for different screen sizes
export const DASHBOARD_COLS = { xl: 12, lg: 12, md: 12, sm: 12, xs: 12 };

// The spacing between widgets in the dashboard
export const DASHBOARD_WIDGETS_SPACING = 20;

// The height for each row. This IS NOT the height of the widget, but only the height of the grid row. For example, if a widget takes up 3 rows
// then the widget's height will be 3*10 = 13px.
export const DASHBOARD_ROW_HEIGHT = 50;

// Minimum width requirement for a dashboard widget. NOTE: This value is NOT in pixel but in number of cols.
export const DASHBOARD_WIDGET_MIN_WIDTH = 2;

// The minimum dashboard widget height in pixel. This is only to be used for calculating DASHBOARD_WIDGET_MIN_HEIGHT
const DASHBOARD_WIDGET_MIN_HEIGHT_IN_PIXEL = 250;

// Minimum height requirement for a dashboard widget. NOTE: This value is NOT in pixel but in number of rows based on the DASHBOARD_ROW_HEIGHT value.
// So if DASHBOARD_ROW_HEIGHT = 50 and DASHBOARD_WIDGET_MIN_HEIGHT = 4 then the actual height is 50 * 4 = 200 pixels (excludes some spacing that
// grid layout add to the widget)
// We don't want the widget to be too small vertically, may be min 250px height. HOWEVER, If ROW_HEIGHT is 50px, that does not mean we should set
// the MIN_HEIGHT to be 5 rows. Because grid layout add 1 spacing in the middle of each row, a widget that has 5 rows high will be
// (5 * 50) + (4 * 20) = 330px (20 is the spacing). A widget with 6 rows high will be (6 * 50) + (5 * 20) = 400px. Therefore, the formula
// to compute the MIN_HEIGHT is a little more complicated than just (250 / 50) = 5. What we want is this
// 250 = (MIN_HEIGHT * ROW_HEIGHT) + ((MIN_HEIGHT - 1) * SPACING)
// => MIN_HEIGHT = (250 - SPACING) / (ROW_HEIGHT + SPACING)
export const DASHBOARD_WIDGET_MIN_HEIGHT = Math.floor(
    (DASHBOARD_WIDGET_MIN_HEIGHT_IN_PIXEL - DASHBOARD_WIDGETS_SPACING) / (DASHBOARD_ROW_HEIGHT + DASHBOARD_WIDGETS_SPACING),
);


// WidgetGroup also use GridLayout to layout the widgets inside the container. However, for inside this widget group, we prefer to have a
// smaller row height so that the user can have better control of the the inner widgets' height.

export const WIDGET_GROUP_WIDGETS_SPACING = 10;

export const WIDGET_GROUP_ROW_HEIGHT = 25;

export const WIDGET_GROUP_WIDGET_MIN_HEIGHT = Math.floor(
    (DASHBOARD_WIDGET_MIN_HEIGHT_IN_PIXEL - WIDGET_GROUP_WIDGETS_SPACING) / (WIDGET_GROUP_ROW_HEIGHT + WIDGET_GROUP_WIDGETS_SPACING),
);

// A wrapper for 'Responsive' component
export const ResponsiveGridLayout = WidthProvider(Responsive);


/**
 * This is the list of Dashboard widget UI that we support. Each widget has a type which specify how the data will be displayed.
 * For example, a widget of type TIME SERIES will display time series data in charts/tabular form.
 */
export enum DashboardWidgetType {
    DATA_INTERVAL = 'data_interval',
    VALUE = 'value',
    HEAT_MAP = 'heat_map',
    ALERTS = 'alerts',
    ENTITIES_MAP = 'entities_map',
    WIDGET_GROUP = 'widget_group',
}


/**
 * This interval type can be used for any widget type that support displaying data over a period of time. These intervals specify the period of time.
 * There are 2 different ways the user can select the interval.
 *      1. By the actual interval e.g. last 1 day, last 1 week, last 1 month, etc...
 *      2. By data resolution, hourly, weekly, monthly, etc... This is when the user want to see 1 data point for each day, week, or month.
 *         Sometime the user doesn't care about the interval, they just want to see the average value per day for example. Internally, when we
 *         fetch data, we still need to fetch data by some interval so we will automatically choose an interval for the user. Base on our
 *         TSDB back end, the interval for these are preset. For example, to get 'hourly' data points, we need to fetch data for an interval
 *         between 28 days and 5 days. So if the user wants to see hourly data, we just use the interval 28 days.
 */
export enum AggregatedDataIntervalType {
    LAST_1_DAY = 'last_1_day',
    LAST_7_DAYS = 'last_7_days',
    LAST_30_DAYS = 'last_30_days',
    LAST_90_DAYS = 'last_90_days',
    MINUTELY = 'minutely',  // The interval for this will be 6 hours
    HOURLY = 'hourly',      // The interval for this will be 15 days
    DAILY = 'daily',        // The interval for this will be 1 year
    WEEKLY = 'weekly',      // The interval for this will be 5 years
    MONTHLY= 'monthly',     // The interval for this will be 10 years
}


/**
 * Interval types used for RAW data. When fetching RAW data, we can only fetch by the number of data points but not the start/end date.
 */
export enum RawDataIntervalType {
    LAST_VALUE = 'last_value',                  // last 1 single value
    LAST_50_VALUES = 'last_50_values',
    LAST_100_VALUES = 'last_100_values',
    LAST_200_VALUES = 'last_200_values',
    LAST_300_VALUES = 'last_300_values',
    LAST_400_VALUES = 'last_400_values',
    LAST_500_VALUES = 'last_500_values',
}



/**
 * Create a predefined interval value in milliseconds based on each AggregatedDataIntervalType type.
 */
export const INTERVAL_VALUE_IN_MS_BY_AGGREGATED_DATA_TYPE = Object.freeze({
    [AggregatedDataIntervalType.LAST_1_DAY]  : Time.DAY_IN_MS,
    [AggregatedDataIntervalType.LAST_7_DAYS] : Time.DAY_IN_MS * 7,
    [AggregatedDataIntervalType.LAST_30_DAYS]: Time.DAY_IN_MS * 30,
    [AggregatedDataIntervalType.LAST_90_DAYS]: Time.DAY_IN_MS * 90,
    [AggregatedDataIntervalType.MINUTELY]    : Time.HOUR_IN_MS * 6,     // 6 * 60 = 260 data points
    [AggregatedDataIntervalType.HOURLY]      : Time.DAY_IN_MS * 15,     // 15 * 24 = 360 data points
    [AggregatedDataIntervalType.DAILY]       : Time.YEAR_IN_MS,         // ~ 365 data points
    [AggregatedDataIntervalType.WEEKLY]      : Time.YEAR_IN_MS * 5,     // 5 * 52 = 260 data points
    [AggregatedDataIntervalType.MONTHLY]     : Time.YEAR_IN_MS * 10,    // 10 * 12 = 120 data points
});



/**
 * Create a predefined LIMIT value based on each RawDataIntervalType type.
 */
export const INTERVAL_VALUE_IN_MS_BY_RAW_DATA_TYPE = Object.freeze({
    [RawDataIntervalType.LAST_VALUE]     : 1,
    [RawDataIntervalType.LAST_50_VALUES] : 50,
    [RawDataIntervalType.LAST_100_VALUES]: 100,
    [RawDataIntervalType.LAST_200_VALUES]: 200,
    [RawDataIntervalType.LAST_300_VALUES]: 300,
    [RawDataIntervalType.LAST_400_VALUES]: 400,
    [RawDataIntervalType.LAST_500_VALUES]: 500,
});



/**
 * When the user want to use data interval widget to display data for a period of time, the user can choose how the data is displayed.
 * They can choose to display data as line chart, bar chart, tabular, etc...
 */
export enum DataIntervalViewType {
    LINE_CHART = 'line_chart',
    BAR_CHART = 'bar_chart',
    AREA_CHART = 'area_chart',
    GANTT_CHART = 'gantt_chart',
    TABULAR = 'tabular'
}


/**
 * When the user want to use ValueDashboardWidget to display the latest time series value, they can choose one of these view types.
 */
export enum SingleValueViewType {
    VALUE = 'value',
    GAUGE = 'gauge',
}


/**
 * Different types of view the user can choose for the Alerts Dashboard widget
 */
export enum AlertsDashboardWidgetViewType {
    TABULAR = 'tabular',
    MAP = 'map',
}


/**
 * Some widget's Data View displays multiple data as separate elements e.g. Value widget. For these widget, the user can configure how they
 * want the elements to be laid out, HORIZONTALLY or VERTICALLY.
 */
export enum WidgetDataViewLayout {
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal'
}


/**
 * To specify how severe the value is based on the threshold, WARNING (not very severe) and SEVERE
 */
export enum ValueThresholdMonitorSeverity {
    WARNING = 'warning',
    CRITICAL = 'critical'
}


/**
 * To specify how the value reached the threshold, too low or too high
 */
export enum ValueThresholdReachingType {
    TOO_LOW = 'too_low',
    TOO_HIGH = 'too_high'
}


/**
 * The data sources' type.
 */
export enum TSDBDataSourceType {
    SIMPLE = 'Simple',
    COLLECTION = 'Collection',
    ENTITY = 'Entity',
}


/**
 * The interface to store Threshold alert when a threshold is reached. When a threshold is reached, we need these info in order to show the UI
 */
export interface ValueThresholdReachedInfo {
    readonly type: ValueThresholdReachingType;
    readonly severity: ValueThresholdMonitorSeverity;
}



/**
 * The interface for dashboard widget's size and position base on 12 columns system.
 */
export interface DashboardWidgetLayout {
    readonly x: number | null;
    readonly y: number | null;
    readonly w: number;
    readonly h: number;
}


/**
 * Base interface for data source settings for each widget. Because this is the base interface, it has only the common props that all data
 * source has, smart module type and ID. All data source settings needs to extends this interface.
 */
export interface DashboardWidgetDataSource {
    readonly homeId: number;
    readonly smartModuleType: SmartModuleType,
}


export interface MetricMetadata {
    readonly displayName?: string | undefined;
    readonly unit?: string | undefined;

    // The settings for when the metric value go below these thresholds
    readonly lowValueWarningThreshold?: number | undefined;
    readonly lowValueCriticalThreshold?: number | undefined;

    // The settings for when the metric value go above these thresholds
    readonly highValueWarningThreshold?: number | undefined;
    readonly highValueCriticalThreshold?: number | undefined;

    // The settings for how many decimal places will be displayed
    readonly decimalPlaces?: number | undefined;

    // The function to use to convert time series/collection values
    readonly conversionFormula?: string | undefined;

    // To manually control the Y Axis range instead letting the chart engine decide on the range based on the data
    readonly yAxisMin?: number | undefined;
    readonly yAxisMax?: number | undefined;

}



export interface MetricsMetadata {
    readonly [metricName: string]: MetricMetadata | undefined;
}


/**
 * This is a more specific type of data source, a data source that get data from TSDB smart modules. However, this is still a generic data source
 * because there are 2 types of time series data, simple series and collections. This is just a BASE interface for more specific time series
 * data source.
 */
export interface TSDBDashboardWidgetDataSource extends DashboardWidgetDataSource {
    readonly dataSourceType: TSDBDataSourceType;
    readonly smartModuleType: SmartModuleType.TSDB;
    readonly metricsMetadata: MetricsMetadata;
    readonly aggregationType: TimeSeriesAggregation | TypeNone;   // TypeNone means to use RAW data
    readonly intervalType: AggregatedDataIntervalType | RawDataIntervalType;

    // We will temporarily keep this
    readonly timeSeriesType: TSDBDataSourceType;
}


/**
 * This is a specific type of TSDBDashboardWidgetDataSource which gets data from SIMPLE time series.
 */
export interface SimpleSeriesDashboardWidgetDataSource extends TSDBDashboardWidgetDataSource {
    readonly dataSourceType: TSDBDataSourceType.SIMPLE;
    readonly smartModuleId: string;
    readonly seriesIds: readonly string[];
}


/**
 * This is a specific type of TSDBDashboardWidgetDataSource which gets data from a COLLECTION.
 */
export interface CollectionDashboardWidgetDataSource extends TSDBDashboardWidgetDataSource {
    readonly dataSourceType: TSDBDataSourceType.COLLECTION;
    readonly smartModuleId: string;
    readonly collectionId: string;
    readonly valueNames?: readonly string[];
    readonly tagNames?: readonly string[];
}


/**
 * This is a specific type of TSDBDashboardWidgetDataSource which gets data from an Entity.
 */
export interface EntityTSDBDashboardWidgetDataSource extends TSDBDashboardWidgetDataSource {
    readonly dataSourceType: TSDBDataSourceType.ENTITY;
    readonly entityId: string;

    // Each entity has multiple metricDefinitions. However, DashboardWidget can only work with 1 Collection therefore, the user need
    // to select 1 of the metricDefinitions from the entity. The user will need to create another widget if they want to show data for another
    // metricDefinition.
    readonly metricsDefinitionId: string;

    readonly valueNames?: readonly string[];
    readonly tagNames?: readonly string[];
}



/**
 * This is the base interface for all widget's view settings. Each type of widget will have its own set of view settings. For example, the
 * DataIntervalDashboardWidget has settings for viewType, showBullets, showLegend, etc...
 * This is only the base interface and there isn't any common props between each widget type therefore this interface is blank. Each
 * type of widget will need to extends this interface.
 */
export interface DashboardWidgetViewSettings {
    readonly metricsMetadata?: MetricsMetadata | undefined;
}



/**
 * This is the interface for the DataIntervalDashboardWidget's VIEW settings.
 */
export interface DataIntervalDashboardWidgetViewSettings extends DashboardWidgetViewSettings {
    readonly viewType: DataIntervalViewType;
    readonly showBullets?: boolean | undefined;             // Default is false
    readonly showLegends?: boolean | undefined;             // Default is false
    readonly showYAxes?: boolean | undefined;               // Default is true
    readonly sortOrder?: SortOrder | undefined;             // Default is "asc"
}


/**
 * Define the set of possible value for label position.
 */
export type LabelPosition = 'top' | 'bottom' | TypeNone;


/**
 * Define a set of value display size, used for font-size. Instead of using type 'number', we use this type to make it more strict
 */
export type ValueDisplaySize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;


/**
 * This is the interface for the ValueDashboardWidget's VIEW settings.
 */
export interface ValueDashboardWidgetViewSettings extends DashboardWidgetViewSettings {
    readonly viewType: SingleValueViewType;
    readonly viewLayout: WidgetDataViewLayout;

    // The position of the label. default is 'top'
    readonly showLabel?: LabelPosition;

    // Control the font-size of the display value however, this is NOT the actual font-size. The valueDisplaySize = 1 is smaller than
    // value '2' which is smaller than the value '3' and so on. What font-size is used for the value '1' will be determined by the widget view comp.
    // For example: If the widget view comp use font-size 20px for valueDisplaySize = 1 then valueDisplaySize 2 should be a font-size larger
    // than valueDisplaySize = 1 which might be 22px etc...
    readonly valueDisplaySize?: ValueDisplaySize | undefined;

    readonly valueDisplayColor?: string | undefined;

    // Whether or not the widget should monitor the value and show some special UI to indicate the value reached the thresholds
    readonly monitorValueThresholds?: boolean | undefined;
}



/**
 * This is the interface for the HeatMapDashboardWidget's VIEW settings.
 */
export interface HeatMapDashboardWidgetViewSettings extends DashboardWidgetViewSettings {
    readonly viewLayout: WidgetDataViewLayout;
    readonly showLegends?: boolean | undefined;             // Default is false
}



/**
 * The data structure for 1 Dashboard Widget object. This is the base interface which contains only the common props that all widget needs.
 * Each individual widget type will have its own interface and define other props that it need.
 */
export interface DashboardWidgetSettings {
    // The widget's ID. This ID is frontend generated ID and we will be using the timestamp of when the widget is created as its' ID
    readonly id: number;

    /**
     * This object specifies where to place a widget in the grid and how big it is. Because a widget can be arranged differently
     * for different screen sizes, we need a different settings for "lg", "md", "sm", "xs" sizes. However, we will only support "lg"
     * for now. If we need to support other screen sizes, we can add more settings later on.
     */
    readonly layouts: {
        readonly lg: DashboardWidgetLayout;
        // Add layout settings for "md", "sm", "xs" if we need to.
    };

    // The ID of the widget of type WidgetGroup which this widget is contained in
    readonly widgetGroupId?: number;

    readonly type: DashboardWidgetType;

    readonly title: string;
    readonly icon?: string | undefined;
}



export interface DataDashboardWidgetSettings extends DashboardWidgetSettings {

    // How often to reload data automatically. Value is in MINUTES and value <= 0 means the widget will not reload data.
    readonly reloadDataFreqInMins: number;

    /**
     * Each widget will need a data source settings that tells the widget UI where to get data from. The interface is for generic widget type
     * therefore it does not specify a more specific data source. Each widget UI type will override this dataSource props with a more specific
     * data source type
     */
    readonly dataSource: DashboardWidgetDataSource;

    readonly viewSettings: DashboardWidgetViewSettings;
}



/**
 * This is a specific Dashboard widget that displays time series data over an interval. Therefore, it works with both
 * SimpleSeriesDashboardWidgetDataSource, CollectionDashboardWidgetDataSource, and EntityDashboardWidgetDataSource
 */
export interface DataIntervalDashboardWidgetSettings extends DataDashboardWidgetSettings {
    readonly type: DashboardWidgetType.DATA_INTERVAL;

    // Replace the dataSource with a more specific type of dataSource, SimpleSeriesDashboardWidgetDataSource | CollectionDashboardWidgetDataSource
    readonly dataSource: SimpleSeriesDashboardWidgetDataSource | CollectionDashboardWidgetDataSource | EntityTSDBDashboardWidgetDataSource;

    // Replace the viewSettings with a more specific type of viewSettings, DataIntervalDashboardWidgetViewSettings
    readonly viewSettings: DataIntervalDashboardWidgetViewSettings;
}


/**
 * This is a specific Dashboard Widget settings that display the latest value of a time series data. This latest
 * value could be an actual measurement value or could be an aggregated value over a period of time.
 */
export interface ValueDashboardWidgetSettings extends DataDashboardWidgetSettings {
    readonly type: DashboardWidgetType.VALUE;

    // Replace the dataSource with a more specific type of dataSource, SimpleSeriesDashboardWidgetDataSource | CollectionDashboardWidgetDataSource
    readonly dataSource: SimpleSeriesDashboardWidgetDataSource | CollectionDashboardWidgetDataSource | EntityTSDBDashboardWidgetDataSource;

    // Replace the viewSettings with a more specific type of viewSettings, ValueDashboardWidgetViewSettings
    readonly viewSettings: ValueDashboardWidgetViewSettings;
}


/**
 * This is a specific Dashboard Widget settings that display time series data over a period of time in Waffle Chart format
 */
export interface HeatMapDashboardWidgetSettings extends DataDashboardWidgetSettings {
    readonly type: DashboardWidgetType.HEAT_MAP;

    // Replace the dataSource with a more specific type of dataSource, SimpleSeriesDashboardWidgetDataSource | CollectionDashboardWidgetDataSource
    readonly dataSource: SimpleSeriesDashboardWidgetDataSource | CollectionDashboardWidgetDataSource | EntityTSDBDashboardWidgetDataSource;

    // Replace the viewSettings with a more specific type of viewSettings, HeatMapDashboardWidgetViewSettings
    readonly viewSettings: HeatMapDashboardWidgetViewSettings;
}


/**
 * This is the interface for the AlertsDashboardWidget's VIEW settings.
 */
export interface AlertsDashboardWidgetViewSettings {
    readonly viewType: AlertsDashboardWidgetViewType;
    readonly pageSize?: number | undefined;
    readonly hideColumns?: readonly string[] | undefined;
}


/**
 * This is a specific Dashboard Widget settings that show alerts
 */
export interface AlertsDashboardWidgetSettings extends DashboardWidgetSettings {
    readonly type: DashboardWidgetType.ALERTS;
    readonly homeId?: number | undefined;
    readonly entityId?: string | undefined;
    readonly viewSettings: AlertsDashboardWidgetViewSettings;
}



/**
 * This is the interface for the EntitiesMapDashboardWidget's VIEW settings.
 */
export interface EntitiesMapDashboardWidgetViewSettings {
    readonly excludeEntitiesWithoutLocation?: boolean | undefined;
}


/**
 * This is a specific Dashboard Widget settings that show entities on a map
 */
export interface EntitiesMapDashboardWidgetSettings extends DashboardWidgetSettings {
    readonly type: DashboardWidgetType.ENTITIES_MAP;
    readonly homeId?: number | undefined;
    readonly entityClasses?: readonly string[] | undefined;
    readonly entityIds?: readonly string[] | undefined;
    readonly viewSettings: EntitiesMapDashboardWidgetViewSettings;
}


/**
 * This is a specific Dashboard Widget settings that group widgets in a container
 */
export interface WidgetGroupWidgetSettings extends DashboardWidgetSettings {
    readonly type: DashboardWidgetType.WIDGET_GROUP;

    // Whether or not to show border/background. The default is FALSE.
    readonly showBackground?: boolean | undefined;
}



/**
 * The interface for 1 Dashboard. A dashboard is a container that contain group of dashboard widgets
 */
export interface Dashboard {
    // The frontend generated ID for the dashboard. We will be using current time
    readonly id: number;

    // The title of the dashboard e.g. 'Monitor Dashboard', 'Sale Dashboard'.
    readonly title: string;

    // Optional subtitle to describe what this dashboard is for
    readonly subtitle?: string | undefined;

    // Optional icon
    readonly icon?: string | undefined;

    readonly widgets?: readonly DashboardWidgetSettings[] | undefined;
}



/**
 * A Type Guard to check if an dataSource object is a DashboardWidgetDataSource
 */
export const isDashboardWidgetDataSource = (obj: unknown): obj is DashboardWidgetDataSource => {
    const dataSource = (obj as DashboardWidgetDataSource);

    // An object is a DashboardWidgetDataSource if it has smartModuleId and smartModuleType
    return dataSource && typeof dataSource.homeId === 'number' && Object.values(SmartModuleType).includes(dataSource.smartModuleType);
};


/**
 * A Type Guard to check if an dataSource object is a TSDBDashboardWidgetDataSource
 */
export const isTSDBDashboardWidgetDataSource = (obj: unknown): obj is TSDBDashboardWidgetDataSource => {
    const dataSource = (obj as TSDBDashboardWidgetDataSource);

    // An object is a TSDBDashboardWidgetDataSource if it is a DashboardWidgetDataSource and its' smartModuleType is
    // specifically as SmartModuleType.TSDB and it has dataSourceType
    return dataSource && isDashboardWidgetDataSource(dataSource) && dataSource.smartModuleType === SmartModuleType.TSDB
        // TODO - Remove 'timeSeriesType' check later once none of the project use old dashboard widget settings
        && (Object.values(TSDBDataSourceType).includes(dataSource.dataSourceType)
            || Object.values(TSDBDataSourceType).includes(dataSource.timeSeriesType))
        && (dataSource.aggregationType === NoneValue || Object.values(TimeSeriesAggregation).includes(dataSource.aggregationType))
        && (Object.values(AggregatedDataIntervalType).includes(dataSource.intervalType as AggregatedDataIntervalType)
            || Object.values(RawDataIntervalType).includes(dataSource.intervalType as RawDataIntervalType));
};


/**
 * A Type Guard to check if an dataSource object is a SimpleSeriesDashboardWidgetDataSource
 */
export const isSimpleSeriesDashboardWidgetDataSource = (obj: unknown): obj is SimpleSeriesDashboardWidgetDataSource => {
    const dataSource = (obj as SimpleSeriesDashboardWidgetDataSource);

    // An object is a SimpleSeriesDashboardWidgetDataSource if it is a TimeSeriesDashboardWidgetDataSource and if it's TSDBDataSourceType
    // is specifically a TSDBDataSourceType.SIMPLE and it has seriesIds
    return dataSource && isTSDBDashboardWidgetDataSource(dataSource)
        // TODO - Remove 'timeSeriesType' check later once none of the project use old dashboard widget settings
        && (dataSource.dataSourceType === TSDBDataSourceType.SIMPLE || dataSource.timeSeriesType === TSDBDataSourceType.SIMPLE)
        && typeof dataSource.smartModuleId === 'string'
        && dataSource.seriesIds instanceof Array
        && dataSource.seriesIds.find((id) => { return typeof id !== 'string'; }) === undefined;   // If none of the ids are NOT string
};


/**
 * A Type Guard to check if an dataSource object is a CollectionDashboardWidgetDataSource
 */
export const isCollectionDashboardWidgetDataSource = (obj: unknown): obj is CollectionDashboardWidgetDataSource => {
    const dataSource = (obj as CollectionDashboardWidgetDataSource);

    // An object is a CollectionDashboardWidgetDataSource if it is a TimeSeriesDashboardWidgetDataSource and if it's TSDBDataSourceType
    // is specifically a TSDBDataSourceType.COLLECTION &&  it has collectionId and it has either valueNames or tagNames
    return dataSource && isTSDBDashboardWidgetDataSource(dataSource)
        // TODO - Remove 'timeSeriesType' check later once none of the project use old dashboard widget settings
        && (dataSource.dataSourceType === TSDBDataSourceType.COLLECTION || dataSource.timeSeriesType === TSDBDataSourceType.COLLECTION)
        && typeof dataSource.smartModuleId === 'string'
        && typeof dataSource.collectionId === 'string'
        && (
            // Either 'valueNames' is an array of strings or tagNames is an array of strings
            (
                dataSource.valueNames instanceof Array
                && dataSource.valueNames.length > 0
                && dataSource.valueNames.find((name) => { return typeof name !== 'string'; }) === undefined
            ) || (
                dataSource.tagNames instanceof Array
                && dataSource.tagNames.length > 0
                && dataSource.tagNames.find((name) => { return typeof name !== 'string'; }) === undefined
            )
        );
};



/**
 * A Type Guard to check if an dataSource object is a EntityDashboardWidgetDataSource
 * @param obj
 */
export const isEntityDashboardWidgetDataSource = (obj: unknown): obj is EntityTSDBDashboardWidgetDataSource => {
    const dataSource = (obj as EntityTSDBDashboardWidgetDataSource);

    // An object is a EntityDashboardWidgetDataSource if it is a isTSDBDashboardWidgetDataSource and it has some of the attributes
    // defined for EntityDashboardWidgetDataSource
    return dataSource && isTSDBDashboardWidgetDataSource(dataSource)
        && dataSource.dataSourceType === TSDBDataSourceType.ENTITY
        && typeof dataSource.entityId === 'string'
        && typeof dataSource.metricsDefinitionId === 'string'
        && (
            // Either 'valueNames' is an array of strings or tagNames is an array of strings
            (
                dataSource.valueNames instanceof Array
                && dataSource.valueNames.length > 0
                && dataSource.valueNames.find((name) => { return typeof name !== 'string'; }) === undefined
            ) || (
                dataSource.tagNames instanceof Array
                && dataSource.tagNames.length > 0
                && dataSource.tagNames.find((name) => { return typeof name !== 'string'; }) === undefined
            )
        );
};


/**
 * @param obj Type guard to check is an obj is a DataIntervalDashboardWidgetViewSettings
 * @returns
 */
export const isDataIntervalDashboardWidgetViewSettings = (obj: unknown): obj is DataIntervalDashboardWidgetViewSettings => {
    const viewSettings = (obj as DataIntervalDashboardWidgetViewSettings);
    return viewSettings !== undefined && Object.values(DataIntervalViewType).includes(viewSettings.viewType)
        && (viewSettings.showYAxes === undefined || typeof viewSettings.showYAxes === 'boolean')
        && (viewSettings.showBullets === undefined || typeof viewSettings.showBullets === 'boolean')
        && (viewSettings.showLegends === undefined || typeof viewSettings.showLegends === 'boolean')
        && (viewSettings.sortOrder === undefined || viewSettings.sortOrder === SortOrder.ASC || viewSettings.sortOrder === SortOrder.DESC);
};



/**
 * Type guard to check is an obj is a ValueDashboardWidgetViewSettings
 */
export const isValueDashboardWidgetViewSettings = (obj: unknown): obj is ValueDashboardWidgetViewSettings => {
    const viewSettings = (obj as ValueDashboardWidgetViewSettings);
    return viewSettings !== undefined && Object.values(SingleValueViewType).includes(viewSettings.viewType)
        && Object.values(WidgetDataViewLayout).includes(viewSettings.viewLayout)
        && (viewSettings.valueDisplaySize === undefined || typeof viewSettings.valueDisplaySize === 'number')
        && (viewSettings.valueDisplayColor === undefined || typeof viewSettings.valueDisplayColor === 'string')
        && (viewSettings.showLabel === undefined || viewSettings.showLabel === 'top'
            || viewSettings.showLabel === 'bottom' || viewSettings.showLabel === 'none');
};


/**
 * Type guard to check is an obj is a HeatMapDashboardWidgetViewSettings
 */
export const isHeatMapDashboardWidgetViewSettings = (obj: unknown): obj is HeatMapDashboardWidgetViewSettings => {
    const viewSettings = (obj as HeatMapDashboardWidgetViewSettings);
    return viewSettings !== undefined && Object.values(WidgetDataViewLayout).includes(viewSettings.viewLayout)
        && (viewSettings.showLegends === undefined || typeof viewSettings.showLegends === 'boolean');
};


/**
 * A Type Guard to check if an Object is a generic DashboardWidgetSettings
 */
export const isDashboardWidgetSettings = (obj: unknown): obj is DashboardWidgetSettings => {
    const widget = (obj as DashboardWidgetSettings);

    // An object is a DashboardWidgetSettings if it has an ID, OPTIONAL homeId, and a layouts props
    return widget && typeof widget.id === 'number' && typeof widget.layouts === 'object'
        && (!widget.icon || typeof widget.icon === 'string');
};



/**
 * Type guard to check is an obj is a AlertDashboardWidgetViewSettings
 */
export const isAlertsDashboardWidgetViewSettings = (obj: unknown): obj is AlertsDashboardWidgetViewSettings => {
    const viewSettings = (obj as AlertsDashboardWidgetViewSettings);
    return viewSettings !== undefined && Object.values(AlertsDashboardWidgetViewType).includes(viewSettings.viewType)
        && (viewSettings.hideColumns === undefined
            || (typeof viewSettings.hideColumns === 'object' && viewSettings.hideColumns instanceof Array))
        && (viewSettings.pageSize === undefined
            || (typeof viewSettings.pageSize === 'number' && Number.isInteger(viewSettings.pageSize) && viewSettings.pageSize > 0));
};


/**
 * A Type Guard to check if an Object is an AlertsDashboardWidgetSettings
 */
export const isAlertsDashboardWidgetSettings = (obj: unknown): obj is AlertsDashboardWidgetSettings => {
    const widget = (obj as AlertsDashboardWidgetSettings);

    return widget && isDashboardWidgetSettings(widget) && widget.type === DashboardWidgetType.ALERTS
        && (!widget.homeId || (typeof widget.homeId === 'number' && Number.isInteger(widget.homeId) && widget.homeId > 0))
        && (!widget.entityId || (typeof widget.entityId === 'string' && widget.entityId.length > 0))
        && (isAlertsDashboardWidgetViewSettings(widget.viewSettings));
};


/**
 * Type guard to check is an obj is a EntitiesMapDashboardWidgetViewSettings
 */
export const isEntitiesMapDashboardWidgetViewSettings = (obj: unknown): obj is EntitiesMapDashboardWidgetViewSettings => {
    const viewSettings = (obj as EntitiesMapDashboardWidgetViewSettings);
    return isObjectLiteral(viewSettings)
        && (viewSettings.excludeEntitiesWithoutLocation === undefined || typeof viewSettings.excludeEntitiesWithoutLocation === 'boolean');
};


/**
 * A Type Guard to check if an Object is an EntitiesMapDashboardWidgetSettings
 */
export const isEntitiesMapDashboardWidgetSettings = (obj: unknown): obj is EntitiesMapDashboardWidgetSettings => {
    const widget = (obj as EntitiesMapDashboardWidgetSettings);

    return widget && isDashboardWidgetSettings(widget) && widget.type === DashboardWidgetType.ENTITIES_MAP
        && (!widget.homeId || (typeof widget.homeId === 'number' && Number.isInteger(widget.homeId) && widget.homeId > 0))
        && (!widget.entityClasses || (widget.entityClasses instanceof Array && widget.entityClasses.every((entityClass) => {
            return typeof entityClass === 'string';
        })))
        && (!widget.entityIds || (widget.entityIds instanceof Array && widget.entityIds.every((entityId) => {
            return typeof entityId === 'string';
        })))
        && (isEntitiesMapDashboardWidgetViewSettings(widget.viewSettings));
};


/**
 * A Type Guard to check if an Object is a generic DashboardWidgetSettings
 */
export const isWidgetGroupWidgetSettings = (obj: unknown): obj is WidgetGroupWidgetSettings => {
    const widget = (obj as WidgetGroupWidgetSettings);

    return widget && isDashboardWidgetSettings(widget) && widget.type === DashboardWidgetType.WIDGET_GROUP
        && (!widget.showBackground || typeof widget.showBackground === 'boolean');
};



/**
 * A Type Guard to check if an Object is a generic DataDashboardWidgetSettings
 */
export const isDataDashboardWidgetSettings = (obj: unknown): obj is DataDashboardWidgetSettings => {
    const widget = (obj as DataDashboardWidgetSettings);

    // An object is a DashboardWidgetSettings if it has an ID, OPTIONAL homeId, and a layouts props
    return widget && isDashboardWidgetSettings(widget) && typeof widget.dataSource === 'object' && typeof widget.viewSettings === 'object';
};



/**
 * Type guard to check if the specified widget is a type of DataIntervalDashboardWidgetSettings, a more specific type of dashboard widget
 */
export const isDataIntervalDashboardWidgetSettings = (obj: unknown): obj is DataIntervalDashboardWidgetSettings => {
    const widget = obj as DashboardWidgetSettings;

    // An object is a DataIntervalDashboardWidgetSettings if it is a DashboardWidgetSettings and its' type is DashboardWidgetType.DATA_INTERVAL
    return widget && isDataDashboardWidgetSettings(widget) && widget.type === DashboardWidgetType.DATA_INTERVAL
        && (isSimpleSeriesDashboardWidgetDataSource(widget.dataSource)
            || isCollectionDashboardWidgetDataSource(widget.dataSource)
            || isEntityDashboardWidgetDataSource(widget.dataSource))
        && isDataIntervalDashboardWidgetViewSettings(widget.viewSettings);
};



/**
 * Type guard to check if the specified widget is a type of ValueDashboardWidgetSettings, a more specific type of dashboard widget
 */
export const isValueDashboardWidgetSettings = (obj: unknown): obj is ValueDashboardWidgetSettings => {
    const widget = obj as DashboardWidgetSettings;

    // An object is a ValueDashboardWidgetSettings if it is a DashboardWidgetSettings and its' type is DashboardWidgetType.VALUE
    return widget && isDataDashboardWidgetSettings(widget) && widget.type === DashboardWidgetType.VALUE
        && (isSimpleSeriesDashboardWidgetDataSource(widget.dataSource)
            || isCollectionDashboardWidgetDataSource(widget.dataSource)
            || isEntityDashboardWidgetDataSource(widget.dataSource))
        && isValueDashboardWidgetViewSettings(widget.viewSettings);
};



/**
 * Type guard to check if the specified widget is a type of HeatMapDashboardWidgetSettings, a more specific type of dashboard widget
 */
export const isHeatMapDashboardWidgetSettings = (obj: unknown): obj is HeatMapDashboardWidgetSettings => {
    const widget = obj as HeatMapDashboardWidgetSettings;

    // An object is a HeatMapDashboardWidgetSettings if it is a DashboardWidgetSettings and its' type is DashboardWidgetType.HEAT_MAP
    return widget && isDataDashboardWidgetSettings(widget) && widget.type === DashboardWidgetType.HEAT_MAP
        && (isSimpleSeriesDashboardWidgetDataSource(widget.dataSource)
            || isCollectionDashboardWidgetDataSource(widget.dataSource)
            || isEntityDashboardWidgetDataSource(widget.dataSource))
        && isHeatMapDashboardWidgetViewSettings(widget.viewSettings);
};


/**
 * Type guard to check if the specified object is a Dashboard
 */
export const isDashboard = (obj: unknown): obj is Dashboard => {
    const dashboard = obj as Dashboard;

    // An object is a HeatMapDashboardWidgetSettings if it is a DashboardWidgetSettings and its' type is DashboardWidgetType.HEAT_MAP
    return dashboard && typeof dashboard.id === 'number' && typeof dashboard.title === 'string'
        && (!dashboard.subtitle || typeof dashboard.subtitle === 'string')
        && (!dashboard.icon || typeof dashboard.icon === 'string')
        && (!dashboard.widgets || dashboard.widgets.find((widget) => {
            return !isDashboardWidgetSettings(widget);
        }) === undefined);
};



/**
 * An interface for key/value pair used for storing Home Dashboard. A HomeDashboard KV is an KeyValuePair which `value` is an Array of
 * DashboardWidgetSettings instead of just any object.
 */
export interface HomeDashboardsKeyValuePair extends Omit<KeyValuePair, 'value'> {
    readonly value: readonly Dashboard[];
}


/**
 * Project.supportToolSettings.dashboardSettings is an Object which doesn't have any structure. However, we will make sure when we
 * read/write to this settings, it will be in this structure
 */
export interface ProjectDashboardSettings {
    readonly dashboards: readonly Dashboard[];
}



/**
 * Type guard to check if the provided kv is a HomeDashboardKeyValuePair
 * @param kv
 */
export const isHomeDashboardsKeyValuePair = (kv: unknown): kv is HomeDashboardsKeyValuePair => {
    const kvValue = (kv as KeyValuePair)?.value;

    // return true as long as the kvValue is an Array. We will validate the items in the array later to make sure the items
    // in the array are valid dashboard widget settings.
    return kvValue !== undefined && kvValue instanceof Array;
};



/**
 * Type guard to check if the provided object is a ProjectDashboardSettings
 * @param object
 */
export const isProjectDashboardsSettings = (object: unknown): object is ProjectDashboardSettings => {
    const dashboardSettings = object as ProjectDashboardSettings;

    // return true as long as the kvValue is an Array. We will validate the items in the array later to make sure the items
    // in the array are valid dashboard widget settings.
    return dashboardSettings !== undefined && dashboardSettings.dashboards instanceof Array && dashboardSettings.dashboards.find((dashboard) => {
        return !isDashboard(dashboard);
    }) === undefined;
};



/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 * This is the interface for the BASE dashboard widget inputs which contains only the basic input values.
 */
export interface DashboardWidgetInputs {
    readonly title?: string | undefined;
    readonly icon?: string | undefined;
}


/**
 * This is the input interface for a more specific type of dashboard that display data.
 */
export interface DataDashboardWidgetInputs {
    readonly reloadDataFreq?: number | undefined;
}


/**
 * This is the INPUTS interface for dashboard widgets that use time series as data source. Because MODE has 2 types of time series, SIMPLE
 * and COLLECTION therefore the user need to specify which type of time series to use when setting up a widget.
 */
export interface TSDBDashboardWidgetDataSourceInputs extends DataDashboardWidgetInputs {
    readonly homeId?: number | undefined;
    readonly dataSourceType?: TSDBDataSourceType | undefined;
    readonly smartModuleId?: string | undefined;
    readonly metricsMetadata?: MetricsMetadata | undefined;
    readonly aggregationType?: TimeSeriesAggregation | TypeNone | undefined;
    readonly intervalType?: AggregatedDataIntervalType | RawDataIntervalType | undefined;

    // for ENTITY data source inputs
    readonly entity?: Entity | undefined;
    readonly metricsDefinition?: EntityMetricsDefinition | undefined;

    // for SIMPLE series data source inputs
    readonly seriesIds?: readonly string[] | undefined;

    // for COLLECTION data source inputs
    readonly collectionId?: string | undefined;

    // for COLLECTION and ENTITY data source inputs
    readonly valueNames?: readonly string[] | undefined;
    readonly tagNames?: readonly string[] | undefined;
}


/**
 * Inputs interface for Alerts Dashboard widget type.
 */
export interface AlertsDashboardWidgetInputs extends DashboardWidgetInputs {
    readonly viewType?: AlertsDashboardWidgetViewType | undefined;
    readonly homeId?: number | undefined;
    readonly entity?: Entity | undefined;
    readonly pageSize?: number | undefined;
    readonly hideColumns?: readonly string[] | undefined;
}


/**
 * Inputs interface for Entities Map Dashboard widget type.
 */
export interface EntitiesMapDashboardWidgetInputs extends DashboardWidgetInputs {
    readonly homeId?: number | undefined;
    readonly entityClasses?: readonly string[] | undefined;
    readonly entities?: readonly Entity[] | undefined;
    readonly excludeEntitiesWithoutLocation?: boolean | undefined;
}


/**
 * Inputs interface for WidgetGroup type.
 */
export interface WidgetGroupDashboardWidgetInputs extends DashboardWidgetInputs {
    readonly showBackground?: boolean | undefined;
}


/**
 * Base interface for DashboardWidgetView inputs. All view inputs will automatically have viewMetricsMetadata.
 */
export interface DashboardWidgetViewSettingsInputs {
    // Because DataSourceInputs already has an input prop call 'metricsMetadata' therefore we have to use a different name for the
    // view's metricsMetadata inputs.
    readonly viewMetricsMetadata?: MetricsMetadata | undefined;
}



/**
 * This is the INPUTS interface for data interval dashboard widgets view settings. It extends DashboardWidgetViewInputs and have its
 * own additional view inputs.
 */
export interface DataIntervalDashboardWidgetViewSettingsInputs extends DashboardWidgetViewSettingsInputs {
    readonly viewType?: DataIntervalViewType | undefined;
    readonly showBullets?: boolean | undefined;
    readonly showLegends?: boolean | undefined;
    readonly showYAxes?: boolean | undefined;
    readonly sortOrder?: SortOrder | undefined;
}


/**
 * This is the INPUTS interface of the SPECIFIC type of dashboard widget, DataIntervalDashboardWidget. It extends the input interface from
 * DashboardWidgetInputs, SimpleSeriesDashboardWidgetDataSourceInputs, and CollectionDashboardWidgetDataSourceInputs in addition to other
 * inputs it has
 */
export interface DataIntervalDashboardWidgetInputs extends
    DashboardWidgetInputs,
    TSDBDashboardWidgetDataSourceInputs,
    DataIntervalDashboardWidgetViewSettingsInputs
{
}



/**
 * This is the INPUTS interface for Single Value dashboard widgets view settings. It extends DashboardWidgetViewInputs and have its
 * own additional view inputs.
 */
export interface ValueDashboardWidgetViewSettingsInputs extends DashboardWidgetViewSettingsInputs {
    readonly viewType?: SingleValueViewType | undefined;
    readonly viewLayout?: WidgetDataViewLayout | undefined;
    readonly showLabel?: LabelPosition | undefined;
    readonly valueDisplaySize?: ValueDisplaySize | undefined;
    readonly valueDisplayColor?: string | undefined;
    readonly monitorValueThresholds?: boolean | undefined;
}



/**
 * This is the INPUTS interface of the SPECIFIC type of dashboard widget, ValueDashboardWidget. It extends the input interface from
 * DashboardWidgetInputs, SimpleSeriesDashboardWidgetDataSourceInputs, and CollectionDashboardWidgetDataSourceInputs in addition to other
 * inputs it has
 */
export interface ValueDashboardWidgetInputs extends
    DashboardWidgetInputs,
    TSDBDashboardWidgetDataSourceInputs,
    ValueDashboardWidgetViewSettingsInputs
{
}


/**
 * This is the INPUTS interface for Heat Map dashboard widgets VIEW settings. It extends DashboardWidgetViewInputs and have its
 * own additional view inputs.
 */
export interface HeatMapDashboardWidgetViewSettingsInputs extends DashboardWidgetViewSettingsInputs {
    readonly viewLayout?: WidgetDataViewLayout | undefined;
    readonly showLegends?: boolean | undefined;
}



/**
 * This is the INPUTS interface of the SPECIFIC type of dashboard widget, HeatMapDashboardWidget. It extends the input interface from
 * DashboardWidgetInputs, SimpleSeriesDashboardWidgetDataSourceInputs, and CollectionDashboardWidgetDataSourceInputs in addition to other
 * inputs it has
 */
export interface HeatMapDashboardWidgetInputs extends
    DashboardWidgetInputs,
    TSDBDashboardWidgetDataSourceInputs,
    HeatMapDashboardWidgetViewSettingsInputs
{
}



/**
 * This is the ERRORS interface for dashboard widget base. It should have the same fields as DashboardWidgetInputs.
 */
export interface DashboardWidgetInputErrors {
    title?: string | undefined;
    icon?: string | undefined;
}


/**
 * This is the ERRORS interface for specific type of dashboard widget which display data
 */
export interface DataDashboardWidgetInputErrors {
    reloadDataFreq?: string;
}


/**
 * This is the ERRORS interface for dashboard widgets that uses TSDB as data source. It should have the same fields as
 * the TSDBDashboardWidgetDataSourceInputs.
 */
export interface TSDBDashboardWidgetDataSourceInputErrors extends DataDashboardWidgetInputErrors {
    homeId?: string | undefined;
    dataSourceType?: string | undefined;
    smartModuleId?: string | undefined;
    metricsMetadata?: {
        [metricName: string]: string | undefined;    // Because we allow multiple metrics, we need to be able to show error per selected metric
    } | undefined;
    aggregationType?: string | undefined;
    intervalType?: string | undefined;
    seriesIds?: string | undefined;
    collectionId?: string | undefined;
    valueNames?: string | undefined;
    tagNames?: string | undefined;
    entity?: string | undefined;
    metricsDefinition?: string | undefined;
}


/**
 * Errors interface associated with the base View Settings inputs interface
 */
export interface DashboardWidgetViewSettingsInputErrors {
    viewMetricsMetadata?: {
        [metricName: string]: string | undefined;    // Because we allow multiple metrics, we need to be able to show error per selected metric
    } | undefined;
}

/**
 * This is the ERRORS interface for Value dashboard widget's viewSettings.
 */
export interface DataIntervalDashboardWidgetViewSettingsInputErrors extends DashboardWidgetViewSettingsInputErrors {
    viewType?: string | undefined;
}


/**
 * This is the ERRORS interface for dashboard widgets type DataIntervalDashboardWidget. It should have the same fields as
 * the DataIntervalDashboardWidgetInputs.
 */
export interface DataIntervalDashboardWidgetInputErrors extends
    DashboardWidgetInputErrors,
    TSDBDashboardWidgetDataSourceInputErrors,
    DataIntervalDashboardWidgetViewSettingsInputErrors
{

}


/**
 * This is the ERRORS interface for Value dashboard widget's viewSettings.
 */

export interface ValueDashboardWidgetViewSettingsInputErrors extends DashboardWidgetViewSettingsInputErrors {
    viewType?: string | undefined;
    viewLayout?: string | undefined;
    showLabel?: string | undefined;
    valueDisplaySize?: string | undefined;
    valueDisplayColor?: string | undefined;
    monitorValueThresholds?: string | undefined;
}


/**
 * This is the ERRORS interface for dashboard widgets type ValueDashboardWidget. It should have the same fields as
 * the ValueDashboardWidgetInputs.
 */
export interface ValueDashboardWidgetInputErrors extends
    DashboardWidgetInputErrors,
    TSDBDashboardWidgetDataSourceInputErrors,
    ValueDashboardWidgetViewSettingsInputErrors
{

}



/**
 * This is the ERRORS interface for HeatMap dashboard widget's viewSettings
 */
export interface HeatMapDashboardWidgetViewSettingsInputErrors extends DashboardWidgetViewSettingsInputErrors {
    viewLayout?: string | undefined;
}


/**
 * This is the ERRORS interface for dashboard widgets type HeatMapDashboardWidget. It should have the same fields as
 * the HeatMapDashboardWidgetInputs.
 */
export interface HeatMapDashboardWidgetInputErrors extends
    DashboardWidgetInputErrors,
    TSDBDashboardWidgetDataSourceInputErrors,
    HeatMapDashboardWidgetViewSettingsInputErrors
{

}

/**
 * Input error interface for Alerts Dashboard widget inputs
 */
export interface AlertsDashboardWidgetInputErrors extends DashboardWidgetInputErrors {
    viewType?: string | undefined;
    homeId?: string | undefined;
    entity?: string | undefined;
    pageSize?: string | undefined;
    hideColumns?: string | undefined;
}


/**
 * Input error interface for Entities Map Dashboard widget inputs
 */
export interface EntitiesMapDashboardWidgetInputErrors extends DashboardWidgetInputErrors {
    homeId?: string | undefined;
    entityClasses?: string | undefined;
    entities?: string | undefined;
}



/**
 * Below are the interfaces used for caching the params we used when fetching data. After fetching data, we need to re-select the data from
 * the data state and we must use the same params we used when fetching data or else we will not be able to find the data from the data state.
 * So just before or after we create an action to fetch data, we will create a object of one of these types, depending on which data we were
 * fetching, and cache it locally in the component. When we need to re-select the data, we will use the cached fetch params.
 */
export interface FetchAggregatedSimpleSeriesDataParams {
    readonly homeId: number;
    readonly smartModuleId: string;
    readonly seriesIds: readonly string[];
    readonly aggregation: TimeSeriesAggregation;
    readonly startTime: string;
    readonly endTime: string;
    readonly succeedTimeSeriesIds: readonly string[];
}

export interface FetchRawSimpleSeriesDataParams {
    readonly homeId: number;
    readonly smartModuleId: string;
    readonly seriesIds: readonly string[];
    readonly endTime: string;
    readonly limit: number;
    readonly succeedTimeSeriesIds: readonly string[];
}

export interface FetchAggregatedCollectionDataParams {
    readonly homeId: number;
    readonly smartModuleId: string;
    readonly collectionId: string;
    readonly aggregation: TimeSeriesAggregation;
    readonly startTime: string;
    readonly endTime: string;
    readonly valueNames: readonly string[];
}

export interface FetchRawCollectionDataParams {
    readonly homeId: number;
    readonly smartModuleId: string;
    readonly collectionId: string;
    readonly endTime: string;
    readonly limit: number;
    readonly valueNames: readonly string[] | undefined;
    readonly tagNames: readonly string[] | undefined;
}

// Type guard functions to check if an object is a specific type of fetch params
export const isFetchAggregatedSimpleSeriesDataParams = (obj: unknown): obj is FetchAggregatedSimpleSeriesDataParams => {
    const fetchParams = obj as FetchAggregatedSimpleSeriesDataParams;
    return fetchParams.seriesIds !== undefined && fetchParams.aggregation !== undefined && fetchParams.startTime !== undefined;
};

export const isFetchRawSimpleSeriesDataParams = (obj: unknown): obj is FetchRawSimpleSeriesDataParams => {
    const fetchParams = obj as FetchRawSimpleSeriesDataParams;
    return fetchParams.seriesIds !== undefined && fetchParams.limit !== undefined;
};

export const isFetchAggregatedCollectionDataParams = (obj: unknown): obj is FetchAggregatedCollectionDataParams => {
    const fetchParams = obj as FetchAggregatedCollectionDataParams;
    return fetchParams.collectionId !== undefined && fetchParams.aggregation !== undefined && fetchParams.startTime !== undefined;
};

export const isFetchRawCollectionDataParams = (obj: unknown): obj is FetchRawCollectionDataParams => {
    const fetchParams = obj as FetchRawCollectionDataParams;
    return fetchParams.collectionId !== undefined && fetchParams.limit !== undefined;
};
